// src/pages/subscription/DiscountDetailPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useSubscriptionDiscount, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import { DiscountFormDialog } from '@/features/subscription/DiscountFormDialog';

function formatLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(dateStr); }
}

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return '--';
  return (amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDiscountValue(type: string, value: number | null | undefined): string {
  if (value == null) return '--';
  switch (type) {
    case 'DISCOUNT_TYPE_PERCENTAGE': return `${value}%`;
    case 'DISCOUNT_TYPE_FIXED_AMOUNT': return `$${formatMoney(value)}`;
    case 'DISCOUNT_TYPE_FREE_PERIOD': return `${value} months free`;
    default: return String(value);
  }
}

export const DiscountDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useSubscriptionDiscount();
  const { t } = useI18n();
  const item = store.currentItem as any;
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    if (id) store.GetDiscount(id);
    return () => { store.clearCurrent(); };
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;
    if (!confirm(t('subscription.deleteDiscountConfirm'))) return;
    try {
      await store.DeleteDiscount(id);
      navigate('/subscriptions/discounts');
    } catch {
      // Error handled by store
    }
  };

  if (store.isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;
  }
  if (store.error) {
    return <><ErrorAlert error={store.error} /><Link to="/subscriptions/discounts" className="mt-2 inline-block text-sm underline text-primary mt-2 inline-block text-sm underline text-primary">{t("common.backToList")}</Link></>;
  }
  if (!item) {
    return <Alert variant="warning" title={t('common.notFound')}><Link to="/subscriptions/discounts" className="mt-2 inline-block text-sm underline text-primary">{t('common.backToList')}</Link></Alert>;
  }

  const applicablePlans: string[] = item.applicable_plans ?? [];

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">Subscriptions</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/subscriptions/discounts" className="text-muted-foreground hover:text-foreground">Discounts</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{String(item.name ?? id)}</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">{String(item.name ?? '')}</h1>
            {item.is_active ? <Badge variant="success" size="sm">Active</Badge> : <Badge variant="secondary" size="sm">Inactive</Badge>}
          </div>
          <p className="text-sm text-muted-foreground mt-1 font-mono">{String(item.code ?? '')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setShowEdit(true)}>{t('common.edit')}</Button>
          <Button variant="danger" onClick={handleDelete}>{t('common.delete')}</Button>
        </div>
      </div>

      <DiscountFormDialog
        open={showEdit}
        discount={item ?? null}
        onClose={() => setShowEdit(false)}
        onSaved={() => { if (id) store.GetDiscount(id); }}
      />

      {/* Basic Info */}
      <Card>
        <CardHeader><CardTitle>Basic Info</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Code</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">{String(item.code ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Name</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.name ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Description</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.description ?? '--')}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Discount Value */}
      <Card>
        <CardHeader><CardTitle>Discount Value</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Type</dt>
              <dd className="text-sm text-foreground col-span-2">
                {item.type ? <Badge variant="primary" size="sm">{formatLabel(item.type, 'DISCOUNT_TYPE_')}</Badge> : '--'}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Value</dt>
              <dd className="text-sm text-foreground col-span-2 font-semibold">{formatDiscountValue(item.type, item.value)}</dd>
            </div>
            {item.type === 'DISCOUNT_TYPE_FIXED_AMOUNT' && (
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">Currency</dt>
                <dd className="text-sm text-foreground col-span-2">{String(item.currency ?? '--')}</dd>
              </div>
            )}
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Duration (months)</dt>
              <dd className="text-sm text-foreground col-span-2">{item.duration_months != null ? String(item.duration_months) : '--'}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Status */}
      <Card>
        <CardHeader><CardTitle>Status</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Active</dt>
              <dd className="text-sm col-span-2">
                {item.is_active ? <Badge variant="success" size="sm">Active</Badge> : <Badge variant="secondary" size="sm">Inactive</Badge>}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">First Time Only</dt>
              <dd className="text-sm col-span-2">
                {item.first_time_only ? <Badge variant="warning" size="sm">Yes</Badge> : <Badge variant="secondary" size="sm">No</Badge>}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Redemptions */}
      <Card>
        <CardHeader><CardTitle>Redemptions</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Current / Max</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String(item.redemption_count ?? item.current_redemptions ?? 0)}
                {item.max_redemptions ? ` / ${item.max_redemptions}` : ' / Unlimited'}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Validity Period */}
      <Card>
        <CardHeader><CardTitle>Validity Period</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Starts At</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.starts_at)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Ends At</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.ends_at)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Applicable Plans */}
      {applicablePlans.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Applicable Plans</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {applicablePlans.map((planId: string) => (
                <Badge key={planId} variant="secondary" size="sm">{planId}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timestamps */}
      <Card>
        <CardHeader><CardTitle>Timestamps</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created At</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.created_at)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Updated At</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.updated_at)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
});

export default DiscountDetailPage;
